import { Button } from '@breezeventures/ui-kit/button';
import { IconButton } from '@breezeventures/ui-kit/icon-button';
import { Icon } from '@breezeventures/ui-kit/next/icon';

import { DOTS } from '../lib/constants';
import type { PaginationItem } from '../lib/use-pagination-range';

type Props = {
  items: PaginationItem[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  size?: 'l' | 'm';
};

export const PageNumbers = ({
  items,
  currentPage,
  totalPages,
  onPageChange,
  size = 'l',
}: Props) => {
  const isPrevDisabled = currentPage <= 0;
  const isNextDisabled = currentPage >= totalPages - 1;

  return (
    <nav className="flex items-center gap-1">
      <IconButton
        type="button"
        size={size}
        variant="ghost"
        disabled={isPrevDisabled}
        aria-label="Previous page"
        onClick={() => onPageChange(currentPage - 1)}
      >
        <Icon
          name="chevron-down"
          size={24}
          version="v1"
          className={`
            size-4 rotate-90 text-primary
            lg:size-6
          `}
        />
      </IconButton>

      <ul className="flex items-center gap-1">
        {items.map((item, index) => {
          if (item === DOTS) {
            return (
              <li
                key={`dots-${index}`}
                className={`
                  inline-flex h-10 min-w-10 items-center justify-center px-1
                  typography-text-font-family text-primitive-accent-brand
                  lg:h-14 lg:min-w-14
                `}
              >
                …
              </li>
            );
          }

          const isCurrent = item === currentPage;

          return (
            <li key={`page-${index}`}>
              <Button
                type="button"
                size={size}
                variant={isCurrent ? 'secondary' : 'ghost'}
                onClick={() => {
                  if (!isCurrent) {
                    onPageChange(item);
                  }
                }}
              >
                {item + 1}
              </Button>
            </li>
          );
        })}
      </ul>

      <IconButton
        type="button"
        size={size}
        variant="ghost"
        disabled={isNextDisabled}
        aria-label="Next page"
        onClick={() => onPageChange(currentPage + 1)}
      >
        <Icon
          name="chevron-down"
          size={24}
          version="v1"
          className={`
            size-4 -rotate-90 text-primary
            lg:size-6
          `}
        />
      </IconButton>
    </nav>
  );
};
